import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import ClientLayout from "../../components/portal/ClientLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import {
  Calendar,
  ExternalLink,
  User,
  Mail,
  AlertCircle,
} from "lucide-react";

const ClientBooking = () => {
  const { token, API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [consultant, setConsultant] = useState(null);
  const [bookingLink, setBookingLink] = useState("");

  useEffect(() => {
    fetchConsultantInfo();
  }, []);

  const fetchConsultantInfo = async () => {
    try {
      // Fetch client dashboard data to get assigned consultant info
      const response = await fetch(`${API}/client/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.consultant) {
          setConsultant(data.consultant);
          setBookingLink(data.consultant.booking_link || "");
        }
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBookingLink = () => {
    if (bookingLink) {
      window.open(bookingLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <ClientLayout activeNav="book appointment">
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-xl font-bold text-rnb-navy">Book an Appointment</h1>
            <p className="text-sm text-gray-400 mt-0.5">Schedule a meeting with your consultant.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-rnb-teal/30 border-t-rnb-teal rounded-full animate-spin"></div>
          </div>
        ) : consultant ? (
          <Card data-testid="booking-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-rnb-blue" />
                Your Consultant
              </CardTitle>
              <CardDescription>
                Book a meeting with your assigned consultant
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Consultant Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-rnb-blue/10 flex items-center justify-center">
                    <User className="h-7 w-7 text-rnb-blue" />
                  </div>
                  <div>
                    <p className="font-semibold text-rnb-navy text-lg">
                      {consultant.name}
                    </p>
                    <p className="text-sm text-rnb-gray flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" />
                      {consultant.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Booking Link Section */}
              {bookingLink ? (
                <div className="space-y-4">
                  <p className="text-rnb-gray text-sm">
                    Click the button below to open your consultant&apos;s calendar and schedule a meeting.
                  </p>
                  <Button
                    onClick={handleOpenBookingLink}
                    className="w-full bg-rnb-blue hover:bg-rnb-navy"
                    size="lg"
                    data-testid="open-booking-link-btn"
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    Schedule Appointment
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                  <p className="text-xs text-center text-rnb-gray">
                    Opens in a new tab
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">
                        Booking link not available
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        Your consultant hasn&apos;t set up their booking calendar yet. 
                        Please reach out to them directly via chat or email to schedule a meeting.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="no-consultant-card">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <User className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-rnb-navy mb-2">
                No Consultant Assigned
              </h3>
              <p className="text-rnb-gray max-w-sm mx-auto">
                You don&apos;t have a consultant assigned to your account yet. 
                Please contact support for assistance.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientBooking;
